import type { GameDbImage } from '../types';

export const DEFAULT_COVER_LOCALIZATION_PRIORITY = [null, 'EU', 'ja-JP'] as const;

const PAL_RELEASE_COUNTRIES = new Set(['EU', 'AU', 'DE', 'ES', 'FI', 'FR', 'IT', 'NZ', 'UK', 'ZA']);

export function getCoverLocalizationPriorityForReleaseCountry(
  releaseCountry: string | null | undefined,
): readonly (string | null)[] {
  const normalizedCountry = releaseCountry?.trim().toUpperCase();

  if (normalizedCountry === 'JP') return ['ja-JP', null, 'EU'];
  if (normalizedCountry === 'KR') return ['ko-KR', null, 'EU', 'ja-JP'];
  if (normalizedCountry && PAL_RELEASE_COUNTRIES.has(normalizedCountry)) return ['EU', null, 'ja-JP'];

  return DEFAULT_COVER_LOCALIZATION_PRIORITY;
}

export interface CoverSelectionOptions {
  localizationPriority?: readonly (string | null)[];
}

type CoverCandidate = {
  type?: string;
  url: string;
  localizationId?: GameDbImage['localizationId'];
  localization?: GameDbImage['localization'];
  providerImageId?: string;
};

const compareStrings = (left = '', right = ''): number => {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

export function selectPreferredCover<T extends CoverCandidate>(
  images: readonly T[] | null | undefined,
  options: CoverSelectionOptions = {},
): T | undefined {
  const covers = images?.filter((image) => image.type === 'cover');
  if (!covers?.length) return undefined;

  const localizationPriority = options.localizationPriority ?? DEFAULT_COVER_LOCALIZATION_PRIORITY;
  const localizationRanks = new Map(localizationPriority.map((localization, index) => [localization, index]));

  return [...covers].sort((left, right) => {
    const leftIsCanonical = left.localizationId == null;
    const rightIsCanonical = right.localizationId == null;
    const leftRegionId = left.localization?.region.id;
    const rightRegionId = right.localization?.region.id;
    const leftLocalization = leftIsCanonical ? null : left.localization?.region.identifier;
    const rightLocalization = rightIsCanonical ? null : right.localization?.region.identifier;
    const leftRank = leftLocalization === undefined ? undefined : localizationRanks.get(leftLocalization);
    const rightRank = rightLocalization === undefined ? undefined : localizationRanks.get(rightLocalization);
    const fallbackRank = localizationPriority.length;
    const leftEffectiveRank = leftRank ?? fallbackRank;
    const rightEffectiveRank = rightRank ?? fallbackRank;

    if (leftEffectiveRank !== rightEffectiveRank) return leftEffectiveRank - rightEffectiveRank;
    if (leftIsCanonical !== rightIsCanonical) return leftIsCanonical ? -1 : 1;

    const identifierComparison = compareStrings(leftLocalization ?? undefined, rightLocalization ?? undefined);
    if (identifierComparison !== 0) return identifierComparison;

    const leftEffectiveRegionId = leftRegionId ?? Number.MAX_SAFE_INTEGER;
    const rightEffectiveRegionId = rightRegionId ?? Number.MAX_SAFE_INTEGER;
    if (leftEffectiveRegionId !== rightEffectiveRegionId) return leftEffectiveRegionId - rightEffectiveRegionId;

    return compareStrings(left.providerImageId, right.providerImageId) || compareStrings(left.url, right.url);
  })[0];
}

export function selectPreferredCoverForReleaseCountry<T extends CoverCandidate>(
  images: readonly T[] | null | undefined,
  releaseCountry: string | null | undefined,
): T | undefined {
  return selectPreferredCover(images, {
    localizationPriority: getCoverLocalizationPriorityForReleaseCountry(releaseCountry),
  });
}
